import { createContext, useState, useEffect, ReactNode } from 'react';
import { DefaultTheme } from 'styled-components';
import { AppData } from '../models/types';
import { SocioServiceFirestore } from '../services/SocioServiceFirestore';
import { LigaServiceFirestore } from '../services/LigaServiceFirestore';
import { SistemaPuntuacionServiceFirestore } from '../services/SistemaPuntuacionServiceFirestore';
import { CarreraServiceFirestore } from '../services/CarreraServiceFirestore';
import { sancionServiceFirestore } from '../services/SancionServiceFirestore';
import { reclamacionServiceFirestore } from '../services/ReclamacionServiceFirestore';
import EquipoService from '../services/EquipoServiceFirestore';

// Define a default theme object, now typed as DefaultTheme
const defaultTheme: DefaultTheme = {
  colors: {
    primary: { main: '#0f3460', light: '#5f78a0', dark: '#001f3f', contrastText: '#ffffff' },
    secondary: { main: '#e94560', light: '#ff798d', dark: '#b00036', contrastText: '#000000' },
    error: { main: '#f44336', light: '#ff7961', dark: '#ba000d', contrastText: '#ffffff' },
    warning: { main: '#ff9800', light: '#ffc947', dark: '#c66900', contrastText: '#000000' },
    info: { main: '#2196f3', light: '#64b5f6', dark: '#1976d2', contrastText: '#ffffff' },
    success: { main: '#4caf50', light: '#81c784', dark: '#388e3c', contrastText: '#000000' },
    background: { default: '#f4f6f8', paper: '#ffffff', dark: '#d1d3d4' },
    text: { primary: '#1a1a2e', secondary: '#6c757d', disabled: '#bdbdbd' },
  },
};

// Crear el contexto para los datos de la aplicación
interface AppContextType {
  data: AppData;
  loading: boolean;
  loadData: () => void;
  saveData: (data: AppData) => void;
  theme: DefaultTheme; // Use DefaultTheme directly
}

const defaultContext: AppContextType = {
  data: {
    ligas: [],
    socios: [],
    carreras: [],
    sistemasPuntuacion: [],
    sanciones: [],
    apelaciones: [],
    eventosComunitarios: [],
    reclamaciones: [],
    reglasSancionesAutomaticas: [],
    equipos: [],
  },
  loading: true,
  loadData: () => {},
  saveData: () => {},
  theme: defaultTheme, // Add theme to default context
};

export const AppContext = createContext<AppContextType>(defaultContext);

// Proveedor del contexto
interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider = ({ children }: AppProviderProps) => {
  const [data, setData] = useState<AppData>(defaultContext.data);
  const [loading, setLoading] = useState<boolean>(defaultContext.loading);

  // Cargar datos al iniciar la aplicación
  useEffect(() => {
    loadData();
  }, []);

  // Función para cargar datos
  const loadData = async () => {
    setLoading(true);
    //console.log('DEBUG - AppContext - Cargando datos desde Firestore...');
    
    try {
      const [socios, ligas, sistemasPuntuacion, carreras, sanciones, reclamaciones, equipos] = await Promise.all([
        SocioServiceFirestore.getAll(),
        LigaServiceFirestore.getAll(),
        SistemaPuntuacionServiceFirestore.getAll(),
        CarreraServiceFirestore.getAll(),
        sancionServiceFirestore.getAll(),
        reclamacionServiceFirestore.getAll(),
        EquipoService.getAll()
      ]);
      
      //console.log('DEBUG - AppContext - Datos cargados de Firestore:');
      //console.log('DEBUG - AppContext - Socios:', socios);
      //console.log('DEBUG - AppContext - Ligas:', ligas);
      //console.log('DEBUG - AppContext - SistemasPuntuacion:', sistemasPuntuacion);
      //console.log('DEBUG - AppContext - Carreras:', carreras);
      //console.log('DEBUG - AppContext - Sanciones:', sanciones);
      //console.log('DEBUG - AppContext - Reclamaciones:', reclamaciones);
      //console.log('DEBUG - AppContext - Equipos:', equipos);
      
      // Normaliza socios para asegurar que todos tengan 'sanciones' como array
      const sociosNorm = socios.map(s => {
        const socioNormalizado = {
          id: s.id ?? '',
          nombre: s.nombre ?? '',
          apellido: s.apellido ?? '',
          guid: s.guid ?? '',
          ligaAsignada: s.ligaAsignada ?? '',
          puntosTotales: s.puntosTotales ?? 0,
          sanciones: [],
          ...s // Añade el resto de campos opcionales, pero los obligatorios van primero
        };
        //console.log(`DEBUG - AppContext - Socio normalizado: ${socioNormalizado.display_name || socioNormalizado.nombre}, Categoria: ${socioNormalizado.Categoria}`);
        return socioNormalizado;
      });
      
      //console.log('DEBUG - AppContext - Total socios normalizados:', sociosNorm.length);
      
      setData(prev => ({
        ...prev,
        socios: sociosNorm,
        ligas,
        sistemasPuntuacion,
        carreras,
        sanciones,
        reclamaciones,
        equipos
      }));
      
      //console.log('DEBUG - AppContext - Estado actualizado con datos de Firestore');
    } catch (error) {
      console.error('ERROR - AppContext - Error al cargar datos:', error);
    } finally {
      setLoading(false);
    }
  };

  // Función para guardar datos (solo actualiza el estado local, la persistencia se gestiona en los servicios Firestore)
  const saveData = (newData: AppData) => {
    setData(newData);
  };

  return (
    <AppContext.Provider value={{ data, loading, loadData, saveData, theme: defaultTheme }}>
      {children}
    </AppContext.Provider>
  );
};
